<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import type { QuestionReply } from '~/types/Feedback';

const props = defineProps<{ reply: QuestionReply; questionId: number }>();

const innerReply = ref(props.reply);
const { userDetails } = storeToRefs(useProfileStore());
const feedbackStore = useFeedbackStore();

const likedByMe = computed(() => innerReply.value.likers.includes(userDetails.value?.user_id as number));
const dislikedByMe = computed(() => innerReply.value.dislikers.includes(userDetails.value?.user_id as number));

const isLoading = ref(false);

async function likeReply() {
  isLoading.value = true;

  if (dislikedByMe.value) {
    const updatedDislikesList = await feedbackStore.dislikeQuestionReply(innerReply.value.id);

    if (updatedDislikesList) innerReply.value.dislikers = updatedDislikesList;
  }

  const updatedLikesList = await feedbackStore.likeQuestionReply(innerReply.value.id);

  if (updatedLikesList) innerReply.value.likers = updatedLikesList;

  isLoading.value = false;
}

async function dislikeReply() {
  isLoading.value = true;

  if (likedByMe.value) {
    const updatedLikesList = await feedbackStore.likeQuestionReply(innerReply.value.id);

    if (updatedLikesList) innerReply.value.likers = updatedLikesList;
  }

  const updatedDislikesList = await feedbackStore.dislikeQuestionReply(innerReply.value.id);

  if (updatedDislikesList) innerReply.value.dislikers = updatedDislikesList;

  isLoading.value = false;
}

const route = useRoute();
const { isLogged } = storeToRefs(useAuthStore());

async function redirectToLogin(type: string) {
  await navigateTo({
    path: route.path,
    hash: `#reply-${props.reply.id}`,
    query: {
      ...route.query,
      tab: 'questions',
      questionId: props.questionId,
      replyId: props.reply.id,
      type,
    },
  });

  localStorage.setItem('return-url', route.fullPath);
  const toast = useToast();
  toast.info({ component: ToastComponent, props: { text: 'Чтобы поставить реакцию, нужно войти' } }, { timeout: 4000 });
  await navigateTo('/login', { external: true });
}

function handleReplyBtnLikeClick() {
  if (isLogged.value) {
    likeReply();
  } else {
    redirectToLogin('like');
  }
}

function handleReplyBtnDislikeClick() {
  if (isLogged.value) {
    dislikeReply();
  } else {
    redirectToLogin('dislike');
  }
}

onMounted(async () => {
  if (isLogged.value && Number(route.query.replyId) === props.reply.id) {
    if (route.query.type === 'like') {
      await likeReply();
    } else if (route.query.type === 'dislike') {
      await dislikeReply();
    }
  }
});
</script>

<template>
  <div
    :id="`reply-${reply.id}`"
    class="product-question product-question__reply"
  >
    <ProductPageFeedbackUserDetailsBlock
      :avatar="innerReply.replier.avatar"
      :created-at="innerReply.created_at"
      :is-manager="innerReply.replier.is_manager"
      :name="innerReply.replier.name"
    />

    <div class="product-question__content">
      <div class="break-words">{{ innerReply.message }}</div>

      <ProductPageFeedbackListItemImagesBlock :images="innerReply.images" />

      <div class="flex justify-end items-center gap-3">
        Ответ полезен?

        <TheBtn
          :color="likedByMe ? 'primary' : 'outline'"
          :disabled="isLoading"
          size="sm"
          @click="handleReplyBtnLikeClick"
        >
          Да {{ innerReply.likers.length || '' }}
        </TheBtn>

        <TheBtn
          :color="dislikedByMe ? 'primary' : 'outline'"
          :disabled="isLoading"
          size="sm"
          @click="handleReplyBtnDislikeClick"
        >
          Нет {{ innerReply.dislikers.length || '' }}
        </TheBtn>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product-question__reply {
  padding: 16px 0 16px 32px;
}
</style>
